// Dashboard widget renderers — KPI / Chart / Table / Stale badge.
// Extracted from `pages/Dashboards.jsx` (iter 41 refactor) — no behaviour change.
import React from "react";
import { makeChronoFormatter } from "@/lib/chrono-ticks";
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, Legend, FunnelChart, Funnel, LabelList,
    ScatterChart, Scatter, ZAxis, Treemap,
    Sankey, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
    AreaChart, Area, ComposedChart,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { TrendingUp, TrendingDown, AlertTriangle } from "lucide-react";
import { CHART_COLORS, PIE_COLORS, fmtNum, fmtAxis, fmtTooltip } from "./chartFormat";
import GeoMapWidget from "./GeoMapWidget";
import {
    LiquidFillWidget, GaugeWidget, CalendarHeatmapWidget, BoxplotWidget,
    NetworkGraphWidget, CandlestickWidget,
} from "./WowCharts";
// iter-121.104 — Shared label resolution (see lib/widget-derivations.js).
import { deriveLegendName, deriveTooltipName } from "@/lib/widget-derivations";

const makeChronoTickFormatter = (data) => makeChronoFormatter(data);

export function StaleBadge({ reason }) {
    return (
        <Badge variant="outline"
            className="border-amber-500/60 text-amber-600 font-mono text-[10px] gap-1"
            title={reason || "Source column no longer in dataset"}>
            <AlertTriangle className="h-3 w-3" /> STALE
        </Badge>
    );
}


export function KpiWidget({ w }) {
    const cfg = w.config || {};
    const { value, unit, delta, sparkline } = cfg;
    const isUnitPrefix = unit === "$";
    // iter-121.51 — honour the widget.format patch (decimals + currency) for
    // legacy KpiWidget rendering. Mirrors prebuilt KpiTile's formatKpi.
    const fmtMeta = (w.format && typeof w.format === "object") ? w.format : null;
    const renderNum = (v) => {
        if (typeof v !== "number" || !Number.isFinite(v)) return fmtNum(v);
        const dec = fmtMeta && Number.isFinite(fmtMeta.decimals) ? Math.max(0, Math.min(6, fmtMeta.decimals)) : null;
        if (dec == null) return fmtNum(v);
        const abs = Math.abs(v);
        if (abs >= 1e9) return (v / 1e9).toFixed(dec) + "B";
        if (abs >= 1e6) return (v / 1e6).toFixed(dec) + "M";
        if (abs >= 1e3) return (v / 1e3).toFixed(dec) + "K";
        if (Number.isInteger(v)) return v.toLocaleString();
        return v.toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec });
    };
    // iter-121.5 — Long-form insight mode.
    // Gallery "insight" items dump a multi-sentence summary into
    // `config.value` (see GalleryPickerDialog.galleryItemToWidget). The
    // default `text-4xl` KPI styling overflows the widget on dashboards.
    // Detect with TWO signals so we don't false-positive on currency
    // strings like "$1,234.56": the widget must (a) be marked as
    // gallery-sourced and (b) carry a long text value.
    const isLongText = (
        !!cfg.from_gallery
        && typeof value === "string"
        && value.length > 40
    );
    if (isLongText) {
        return (
            <div className="dash-card p-5 h-full flex flex-col overflow-hidden" data-testid={`widget-${w.id}`}>
                <div className="eyebrow mb-1.5">{w.title}</div>
                {/* Scroll-on-overflow so the widget boundary is always respected,
                    regardless of grid-cell height. `whitespace-pre-line` honours
                    \n inside the summary; `leading-relaxed` matches the body
                    reading rhythm defined in the design guidelines. */}
                <div
                    className="text-sm leading-relaxed text-foreground/90 whitespace-pre-line overflow-y-auto pr-1 max-h-full"
                    data-testid={`widget-${w.id}-insight-text`}
                >
                    {value}
                </div>
                {/* Sparkline is intentionally suppressed in long-text mode — it
                    looks broken under a paragraph and a "Sense AI insight" is
                    qualitative, not a metric to spark. */}
            </div>
        );
    }
    return (
        <div className="dash-card p-5 h-full flex flex-col" data-testid={`widget-${w.id}`}>
            <div className="eyebrow mb-1.5">{w.title}</div>
            <div className="flex items-baseline gap-1 mt-1">
                <span className="kpi-number text-3xl md:text-4xl font-medium">
                    {isUnitPrefix ? unit : ""}{renderNum(value)}
                </span>
                {!isUnitPrefix && unit && <span className="text-sm font-mono text-muted-foreground">{unit}</span>}
            </div>
            {delta !== undefined && delta !== null && (
                <div className="mt-2 flex items-center gap-1 text-xs font-mono">
                    {delta >= 0 ? <TrendingUp className="h-3 w-3 text-[hsl(var(--success))]" /> : <TrendingDown className="h-3 w-3 text-primary" />}
                    <span className={delta >= 0 ? "text-[hsl(var(--success))]" : "text-primary"}>
                        {delta >= 0 ? "+" : ""}{delta}%
                    </span>
                    <span className="text-muted-foreground">vs prev period</span>
                </div>
            )}
            {sparkline && sparkline.length > 1 && (
                <div className="mt-auto pt-3 h-10" style={{ minWidth: 60 }}>
                    <ResponsiveContainer width="100%" height="100%" minHeight={32}>
                        <LineChart data={sparkline.map((v, i) => ({ i, v }))}>
                            <Line type="monotone" dataKey="v" stroke="var(--chart-1, hsl(var(--primary)))" strokeWidth={1.5} dot={false} isAnimationActive={false} />
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}


export function ChartWidget({ w }) {
    const cfg = w.config || {};
    const { chart_type = "bar", y_is_currency = false } = cfg;
    const data = cfg.data || [];
    const stale = cfg.stale;
    const labelLong = data.some((d) => String(d?.label ?? "").length > 8);
    const chronoFormatter = makeChronoTickFormatter(data);
    const tickFormatter = chronoFormatter || fmtAxis;
    const xAxisAngle = chronoFormatter ? -45 : (labelLong ? -35 : 0);
    const xAxisTextAnchor = chronoFormatter ? "end" : (labelLong ? "end" : "middle");
    const xAxisHeight = chronoFormatter ? 58 : (labelLong ? 48 : 24);
    // iter-121.104 — Honour `w.options.legend_label` + `w.options.tooltip_label`
    // via the shared resolver so any future renderer additions inherit the
    // same precedence ladder. Falls back to widget title, then "Value" —
    // never to the raw dataKey "value" (the recharts default if `name`
    // is omitted on a series component).
    const legendName = deriveLegendName(w);
    const tooltipName = deriveTooltipName(w);
    // iter-121.128c — Multi-series detection. When `w.series` carries 2+
    // entries, the resolver returns rows shaped `[{label, "Spend": …,
    // "Revenue": …}]` and we render ONE <Line>/<Bar>/<Area> per series.
    // Single-series widgets keep the legacy `dataKey="value"` path so
    // every existing dashboard renders identically.
    const seriesDefs = Array.isArray(w?.series) ? w.series : [];
    const isMultiSeries = (seriesDefs.length > 1 && (chart_type === "line" || chart_type === "area" || chart_type === "bar" || chart_type === "horizontal_bar")) || chart_type === "combo" || chart_type === "composed";
    // iter-121.129b — Multi-series tooltip fix. Recharts invokes the
    // formatter ONCE PER series and passes the series `name` so the
    // tooltip can show "Spend: 1.2K / Revenue: 3.4K". Returning the
    // widget-level `tooltipName` for every series flattens them all
    // to the same label and makes the hover unreadable — the exact
    // "hover labels not updating" regression the user reported.
    const tooltipFmtWithName = (v, name) => {
        const isPct = name && (name.toLowerCase().includes("yoy") || name.toLowerCase().includes("%") || name.toLowerCase().includes("growth"));
        const display = isPct ? `${fmtNum(v)}%` : fmtTooltip(v, y_is_currency);
        return [display, isMultiSeries ? (name || tooltipName) : tooltipName];
    };
    // iter-119al — explicit empty-state. When the resolver has no rows yet
    // (new tenant, blank dashboard freshly populated from Gallery, source not
    // attached) recharts otherwise renders an axes-only ghost frame which
    // looks broken. Surface a clear "No data yet" panel instead so the user
    // knows what to do next.
    const isEmpty = !data.length;
    if (isEmpty) {
        return (
            <div className="dash-card p-5 h-full flex flex-col" data-testid={`widget-${w.id}`}>
                <div className="flex items-start justify-between mb-1">
                    <div className="eyebrow">{w.title}</div>
                    {stale && <StaleBadge reason={cfg.stale_reason} />}
                </div>
                <div className="flex-1 mt-3 min-h-0 flex flex-col items-center justify-center text-center gap-2 px-4">
                    <div className="text-xs text-muted-foreground max-w-sm">
                        No data yet for this widget.
                    </div>
                    <div className="text-[10px] font-mono text-muted-foreground/70 uppercase tracking-wider">
                        Connect a source or wait for the next sync to populate it.
                    </div>
                </div>
            </div>
        );
    }

    if (chart_type === "funnel") {
        const stages = data.map((d, i) => ({
            name: d.label,
            value: Number(d.value) || 0,
            fill: CHART_COLORS[i % CHART_COLORS.length],
        }));
        return (
            <div className="dash-card p-5 h-full flex flex-col" data-testid={`widget-${w.id}`}>
                <div className="flex items-start justify-between mb-1">
                    <div className="eyebrow">{w.title}</div>
                    {stale && <StaleBadge reason={cfg.stale_reason} />}
                </div>
                <div className="flex-1 mt-3 min-h-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <FunnelChart>
                            <Tooltip formatter={(v) => fmtTooltip(v, false)}
                                contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }} />
                            <Funnel dataKey="value" data={stages} isAnimationActive>
                                <LabelList dataKey="name" position="right" fontSize={11} fill="hsl(var(--foreground))" />
                                <LabelList dataKey="value" position="center" fontSize={11} fill="hsl(var(--primary-foreground))" formatter={(v) => fmtAxis(v)} />
                            </Funnel>
                        </FunnelChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    }

    if (chart_type === "geo_map") {
        return <GeoMapWidget w={w} />;
    }

    // iter-119bg Phase 7 — Scatter wow chart. Expects each row as
    // {x: number, y: number, label?, size?} in cfg.data OR the legacy
    // {label, value} shape (we fall back to plotting value vs row index).
    if (chart_type === "scatter") {
        const points = data.map((d, i) => ({
            x: typeof d.x === "number" ? d.x : (typeof d.value === "number" ? d.value : i),
            y: typeof d.y === "number" ? d.y : (typeof d.value === "number" ? d.value : Number(d.value) || 0),
            label: d.label ?? d.name ?? `#${i + 1}`,
            size: typeof d.size === "number" ? d.size : 50,
        }));
        return (
            <div className="dash-card p-5 h-full flex flex-col" data-testid={`widget-${w.id}`}>
                <div className="flex items-start justify-between mb-1">
                    <div className="eyebrow">{w.title}</div>
                    {stale && <StaleBadge reason={cfg.stale_reason} />}
                </div>
                <div className="flex-1 mt-3 min-h-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <ScatterChart margin={{ top: 4, right: 16, left: 0, bottom: 8 }}>
                            <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                            <XAxis type="number" dataKey="x" fontSize={10} stroke="hsl(var(--muted-foreground))"
                                tickFormatter={fmtAxis} name={cfg.x_label || cfg.x || "x"} />
                            <YAxis type="number" dataKey="y" fontSize={10} stroke="hsl(var(--muted-foreground))"
                                tickFormatter={fmtAxis} name={cfg.y_label || cfg.y || "y"} />
                            <ZAxis type="number" dataKey="size" range={[40, 200]} />
                            <Tooltip cursor={{ strokeDasharray: "3 3" }}
                                contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }}
                                formatter={(v) => fmtTooltip(v, y_is_currency)}
                                labelFormatter={(_, p) => (p && p[0] && p[0].payload && p[0].payload.label) || ""} />
                            <Scatter data={points} fill={CHART_COLORS[0]} />
                        </ScatterChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    }

    // iter-119bg Phase 7 — Treemap wow chart. Recharts native; expects
    // data as [{label, value, fill?}].
    if (chart_type === "treemap") {
        const tree = data.map((d, i) => ({
            name: d.label ?? d.name ?? `#${i + 1}`,
            size: Number(d.value) || 0,
            fill: d.fill || CHART_COLORS[i % CHART_COLORS.length],
        }));
        return (
            <div className="dash-card p-5 h-full flex flex-col" data-testid={`widget-${w.id}`}>
                <div className="flex items-start justify-between mb-1">
                    <div className="eyebrow">{w.title}</div>
                    {stale && <StaleBadge reason={cfg.stale_reason} />}
                </div>
                <div className="flex-1 mt-3 min-h-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <Treemap data={tree} dataKey="size" stroke="hsl(var(--background))" fill={CHART_COLORS[0]} aspectRatio={4 / 3} isAnimationActive={false}>
                            <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }}
                                formatter={(v) => fmtTooltip(v, y_is_currency)} />
                        </Treemap>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    }

    // iter-119bg Phase 7 — Heatmap wow chart. Recharts has no native
    // heatmap so we use a CSS grid with HSL-tinted cells. Expects either:
    //   cfg.data = [{x, y, value}] (long format)
    //   OR  cfg.matrix = { rows: [...], cols: [...], cells: [[v,...], ...] }
    if (chart_type === "heatmap") {
        let rows = [];
        let cols = [];
        let cellMap = {};
        if (cfg.matrix && Array.isArray(cfg.matrix.rows) && Array.isArray(cfg.matrix.cols)) {
            rows = cfg.matrix.rows;
            cols = cfg.matrix.cols;
            const cells = cfg.matrix.cells || [];
            for (let r = 0; r < rows.length; r++) {
                for (let c = 0; c < cols.length; c++) {
                    cellMap[`${rows[r]}::${cols[c]}`] = Number((cells[r] || [])[c] ?? 0);
                }
            }
        } else {
            const rowSet = new Set();
            const colSet = new Set();
            data.forEach((d) => {
                const rowKey = d.row ?? d.y ?? d.group ?? "";
                const colKey = d.col ?? d.x ?? d.label ?? "";
                rowSet.add(rowKey);
                colSet.add(colKey);
                cellMap[`${rowKey}::${colKey}`] = Number(d.value) || 0;
            });
            rows = Array.from(rowSet);
            cols = Array.from(colSet);
        }
        const values = Object.values(cellMap);
        const maxV = Math.max(...values, 0) || 1;
        const minV = Math.min(...values, 0);
        return (
            <div className="dash-card p-5 h-full flex flex-col" data-testid={`widget-${w.id}`}>
                <div className="flex items-start justify-between mb-1">
                    <div className="eyebrow">{w.title}</div>
                    {stale && <StaleBadge reason={cfg.stale_reason} />}
                </div>
                <div className="flex-1 mt-3 min-h-0 overflow-auto">
                    <table className="text-[10px] font-mono w-full border-collapse" data-testid={`widget-${w.id}-heatmap`}>
                        <thead>
                            <tr>
                                <th className="px-1 py-1 text-muted-foreground"></th>
                                {cols.map((c) => (
                                    <th key={String(c)} className="px-2 py-1 text-muted-foreground font-medium text-center">{String(c)}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={String(r)}>
                                    <td className="px-2 py-1 text-muted-foreground font-medium whitespace-nowrap">{String(r)}</td>
                                    {cols.map((c) => {
                                        const v = cellMap[`${r}::${c}`] ?? 0;
                                        const intensity = maxV > minV ? (v - minV) / (maxV - minV) : 0;
                                        const bg = `hsla(var(--primary-h, 25), 90%, 55%, ${0.10 + intensity * 0.85})`;
                                        return (
                                            <td key={`${r}::${c}`} className="px-2 py-1 text-center border border-border/30"
                                                style={{ backgroundColor: bg, color: intensity > 0.6 ? "white" : "inherit" }}
                                                title={`${String(r)} × ${String(c)}: ${fmtNum(v)}`}>
                                                {fmtAxis(v)}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    // iter-119bh Phase 8 — Liquid fill / Gauge / Calendar heatmap / Boxplot
    if (chart_type === "liquid_fill") return <LiquidFillWidget w={w} stale={stale} />;
    if (chart_type === "gauge") return <GaugeWidget w={w} stale={stale} />;
    if (chart_type === "calendar_heatmap") return <CalendarHeatmapWidget w={w} stale={stale} />;
    if (chart_type === "boxplot") return <BoxplotWidget w={w} stale={stale} />;

    // iter-119bi Phase 9 — Network graph + Candlestick (custom SVG)
    if (chart_type === "network_graph") return <NetworkGraphWidget w={w} stale={stale} />;
    if (chart_type === "candlestick") return <CandlestickWidget w={w} stale={stale} />;

    // iter-119bh Phase 8 — Sankey (recharts native)
    if (chart_type === "sankey") {
        // Expects cfg.data = {nodes: [{name}], links: [{source, target, value}]}
        const sankeyData = (cfg.data && cfg.data.nodes && cfg.data.links)
            ? cfg.data
            : { nodes: [], links: [] };
        return (
            <div className="dash-card p-5 h-full flex flex-col" data-testid={`widget-${w.id}`}>
                <div className="flex items-start justify-between mb-1">
                    <div className="eyebrow">{w.title}</div>
                    {stale && <StaleBadge reason={cfg.stale_reason} />}
                </div>
                <div className="flex-1 mt-3 min-h-0">
                    {sankeyData.nodes.length ? (
                        <ResponsiveContainer width="100%" height="100%">
                            <Sankey data={sankeyData} nodePadding={20} margin={{ left: 4, right: 4 }}
                                link={{ stroke: CHART_COLORS[0] }} node={{ stroke: CHART_COLORS[0], fill: CHART_COLORS[0] }}>
                                <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }}
                                    formatter={(v) => fmtTooltip(v, y_is_currency)} />
                            </Sankey>
                        </ResponsiveContainer>
                    ) : (
                        <div className="flex items-center justify-center h-full text-xs text-muted-foreground">No flow data</div>
                    )}
                </div>
            </div>
        );
    }

    // iter-119bi Phase 9 — Radar (recharts native)
    if (chart_type === "radar") {
        return (
            <div className="dash-card p-5 h-full flex flex-col" data-testid={`widget-${w.id}`}>
                <div className="flex items-start justify-between mb-1">
                    <div className="eyebrow">{w.title}</div>
                    {stale && <StaleBadge reason={cfg.stale_reason} />}
                </div>
                <div className="flex-1 mt-3 min-h-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <RadarChart data={data} outerRadius="75%">
                            <PolarGrid stroke="hsl(var(--border))" />
                            <PolarAngleAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={10} />
                            <PolarRadiusAxis stroke="hsl(var(--muted-foreground))" fontSize={9} tickFormatter={fmtAxis} />
                            <Radar dataKey="value" stroke={CHART_COLORS[0]} fill={CHART_COLORS[0]} fillOpacity={0.45} />
                            <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }}
                                formatter={(v) => fmtTooltip(v, y_is_currency)} />
                        </RadarChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    }

    // iter-119bi Phase 9 — Stacked area (recharts native; multi-series)
    if (chart_type === "stacked_area") {
        // Expects each row to carry one or more series keys. cfg.series_keys
        // lists them (e.g. ["organic", "paid", "direct"]).
        const seriesKeys = Array.isArray(cfg.series_keys) && cfg.series_keys.length
            ? cfg.series_keys
            : (data[0] ? Object.keys(data[0]).filter((k) => k !== "label" && k !== "name") : []);
        return (
            <div className="dash-card p-5 h-full flex flex-col" data-testid={`widget-${w.id}`}>
                <div className="flex items-start justify-between mb-1">
                    <div className="eyebrow">{w.title}</div>
                    {stale && <StaleBadge reason={cfg.stale_reason} />}
                </div>
                <div className="flex-1 mt-3 min-h-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart data={data} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                            <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                            <XAxis dataKey="label" fontSize={10} stroke="hsl(var(--muted-foreground))" />
                            <YAxis fontSize={10} stroke="hsl(var(--muted-foreground))" tickFormatter={fmtAxis} />
                            <Tooltip contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }}
                                formatter={(v) => fmtTooltip(v, y_is_currency)} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            {seriesKeys.map((k, i) => (
                                <Area key={k} type="monotone" dataKey={k} stackId="1"
                                    stroke={CHART_COLORS[i % CHART_COLORS.length]}
                                    fill={CHART_COLORS[i % CHART_COLORS.length]} fillOpacity={0.55} />
                            ))}
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    }

    if (chart_type === "donut" || chart_type === "pie") {
        const total = data.reduce((a, b) => a + (Number(b.value) || 0), 0) || 1;
        const innerRadius = chart_type === "donut" ? "55%" : "0%";
        return (
            <div className="dash-card p-5 h-full flex flex-col" data-testid={`widget-${w.id}`}>
                <div className="flex items-start justify-between mb-1">
                    <div className="eyebrow">{w.title}</div>
                    {stale && <StaleBadge reason={cfg.stale_reason} />}
                </div>
                <div className="flex-1 mt-3 min-h-0">
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie data={data} dataKey="value" nameKey="label" innerRadius={innerRadius} outerRadius="85%" strokeWidth={0}
                                label={({ value }) => `${Math.round((value / total) * 100)}%`}>
                                {data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                            </Pie>
                            <Tooltip
                                content={({ active, payload }) => {
                                    if (!active || !payload || !payload.length) return null;
                                    const item = payload[0];
                                    const display = fmtTooltip(item.value, y_is_currency);
                                    return (
                                        <div className="border border-border bg-card p-2 rounded shadow-sm text-xs">
                                            <div className="font-normal mb-0.5 text-foreground">{item.name}</div>
                                            <div className="flex items-center gap-1" style={{ color: item.color || "hsl(var(--primary))" }}>
                                                <span>{tooltipName}</span>
                                                <span>:</span>
                                                <span className="font-medium">{display}</span>
                                            </div>
                                        </div>
                                    );
                                }}
                            />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    }

    return (
        <div className="dash-card p-5 h-full flex flex-col" data-testid={`widget-${w.id}`}>
            <div className="flex items-start justify-between mb-1">
                <div className="eyebrow">{w.title}</div>
                {stale && <StaleBadge reason={cfg.stale_reason} />}
            </div>
            <div className="flex-1 mt-3 min-h-0">
                <ResponsiveContainer width="100%" height="100%">
                    {chart_type === "combo" || chart_type === "composed" ? (
                        <ComposedChart data={data} margin={{ top: 4, right: 12, left: 0, bottom: labelLong ? 30 : 4 }}>
                            <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                            <XAxis dataKey="label" fontSize={10} stroke="hsl(var(--muted-foreground))"
                                tickFormatter={tickFormatter}
                                angle={xAxisAngle} textAnchor={xAxisTextAnchor} height={xAxisHeight}
                                {...(chronoFormatter ? { interval: 0 } : {})} />
                            <YAxis yAxisId="left" fontSize={10} stroke="hsl(var(--muted-foreground))" tickFormatter={fmtAxis} />
                            <YAxis yAxisId="right" orientation="right" fontSize={10} stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `${v}%`} />
                            <Tooltip formatter={tooltipFmtWithName}
                                contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            {seriesDefs.map((s, i) => {
                                const yAxisId = s.y_axis === "right" ? "right" : "left";
                                const color = s.color || CHART_COLORS[i % CHART_COLORS.length];
                                if (s.render_as === "line") {
                                    return (
                                        <Line key={s.name || i} yAxisId={yAxisId} type="monotone" dataKey={s.name} name={s.name}
                                            stroke={color} strokeWidth={2} dot={{ r: 2 }} />
                                    );
                                } else {
                                    return (
                                        <Bar key={s.name || i} yAxisId={yAxisId} dataKey={s.name} name={s.name}
                                            fill={color} radius={[2, 2, 0, 0]}
                                            /* ISSUE-FIX: Robustly support stacking multiple series in ComposedChart from widget options/config. */
                                            stackId={w.options?.stacked || w.stacked || cfg.stacked ? "a" : undefined} />
                                    );
                                }
                            })}
                        </ComposedChart>
                    ) : chart_type === "line" ? (
                        <LineChart data={data} margin={{ top: 4, right: 12, left: 0, bottom: labelLong ? 30 : 4 }}>
                            <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                            <XAxis dataKey="label" fontSize={10} stroke="hsl(var(--muted-foreground))"
                                tickFormatter={tickFormatter}
                                angle={xAxisAngle} textAnchor={xAxisTextAnchor} height={xAxisHeight}
                                {...(chronoFormatter ? { interval: 0 } : {})} />
                            <YAxis fontSize={10} stroke="hsl(var(--muted-foreground))" tickFormatter={fmtAxis} />
                            <Tooltip formatter={tooltipFmtWithName}
                                contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }} />
                            {/* iter-121.104 — Always render the legend so a custom `legend_label` is visible. */}
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            {/* iter-121.128c — Multi-series: one <Line> per series, dataKey
                                = series name. Single-series falls back to legacy `value`. */}
                            {isMultiSeries ? (
                                seriesDefs.map((s, i) => (
                                    <Line key={s.name || i} type="monotone" dataKey={s.name} name={s.name}
                                        stroke={s.color || CHART_COLORS[i % CHART_COLORS.length]}
                                        strokeWidth={2} dot={{ r: 2 }} />
                                ))
                            ) : (
                                <Line type="monotone" dataKey="value" name={legendName} stroke={CHART_COLORS[0]} strokeWidth={2} dot={{ r: 2 }} />
                            )}
                        </LineChart>
                    ) : chart_type === "area" ? (
                        // iter-121.128c — Area chart, single OR stacked multi-series.
                        // Recharts stackId="1" stacks all areas; remove to overlay.
                        // Default to stacking because "Revenue + Cost stacked area"
                        // matches user intent for the canonical phrasing.
                        <AreaChart data={data} margin={{ top: 4, right: 12, left: 0, bottom: labelLong ? 30 : 4 }}>
                            <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                            <XAxis dataKey="label" fontSize={10} stroke="hsl(var(--muted-foreground))"
                                tickFormatter={tickFormatter}
                                angle={xAxisAngle} textAnchor={xAxisTextAnchor} height={xAxisHeight}
                                {...(chronoFormatter ? { interval: 0 } : {})} />
                            <YAxis fontSize={10} stroke="hsl(var(--muted-foreground))" tickFormatter={fmtAxis} />
                            <Tooltip formatter={tooltipFmtWithName}
                                contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            {isMultiSeries ? (
                                seriesDefs.map((s, i) => (
                                    <Area key={s.name || i} type="monotone" dataKey={s.name} name={s.name} stackId="1"
                                        stroke={s.color || CHART_COLORS[i % CHART_COLORS.length]}
                                        fill={s.color || CHART_COLORS[i % CHART_COLORS.length]}
                                        fillOpacity={0.55} />
                                ))
                            ) : (
                                <Area type="monotone" dataKey="value" name={legendName}
                                    stroke={CHART_COLORS[0]} fill={CHART_COLORS[0]} fillOpacity={0.45} />
                            )}
                        </AreaChart>
                    ) : chart_type === "horizontal_bar" ? (
                        <BarChart layout="vertical" data={data} margin={{ top: 4, right: 12, left: 50, bottom: 4 }}>
                            <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                            <XAxis type="number" fontSize={10} stroke="hsl(var(--muted-foreground))" tickFormatter={fmtAxis} />
                            <YAxis type="category" dataKey="label" fontSize={10} stroke="hsl(var(--muted-foreground))" width={48} />
                            <Tooltip formatter={tooltipFmtWithName}
                                contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            {isMultiSeries ? (
                                seriesDefs.map((s, i) => (
                                    <Bar key={s.name || i} dataKey={s.name} name={s.name}
                                        fill={s.color || CHART_COLORS[i % CHART_COLORS.length]}
                                        radius={[0, 2, 2, 0]}
                                        /* ISSUE-FIX: Robustly support stacking multiple series in horizontal BarChart from widget options/config. */
                                        stackId={w.options?.stacked || w.stacked || cfg.stacked ? "a" : undefined} />
                                ))
                            ) : Array.isArray(cfg.groups) && cfg.groups.length > 0 ? (
                                cfg.groups.map((g, i) => (
                                    <Bar key={g} dataKey={g} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[0, 2, 2, 0]}
                                        /* ISSUE-FIX: Robustly support stacking groups in horizontal BarChart from widget options/config. */
                                        stackId={w.options?.stacked || w.stacked || cfg.stacked ? "a" : undefined} />
                                ))
                            ) : (
                                <Bar dataKey="value" name={legendName} radius={[0, 2, 2, 0]}>
                                    {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                                </Bar>
                            )}
                        </BarChart>
                    ) : isMultiSeries ? (
                        // iter-121.128c — Grouped multi-series bar.
                        <BarChart data={data} margin={{ top: 4, right: 12, left: 0, bottom: labelLong ? 30 : 4 }}>
                            <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                            <XAxis dataKey="label" fontSize={10} stroke="hsl(var(--muted-foreground))"
                                tickFormatter={tickFormatter}
                                angle={xAxisAngle} textAnchor={xAxisTextAnchor} height={xAxisHeight} interval={0} />
                            <YAxis fontSize={10} stroke="hsl(var(--muted-foreground))" tickFormatter={fmtAxis} />
                            <Tooltip formatter={(v) => fmtTooltip(v, y_is_currency)}
                                contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            {seriesDefs.map((s, i) => (
                                <Bar key={s.name || i} dataKey={s.name} name={s.name}
                                    fill={s.color || CHART_COLORS[i % CHART_COLORS.length]}
                                    radius={[2, 2, 0, 0]}
                                    /* ISSUE-FIX: Robustly support stacking multiple series in vertical BarChart from widget options/config. */
                                    stackId={w.options?.stacked || w.stacked || cfg.stacked ? "a" : undefined} />
                            ))}
                        </BarChart>
                    ) : Array.isArray(cfg.groups) && cfg.groups.length > 0 ? (
                        <BarChart data={data} margin={{ top: 4, right: 12, left: 0, bottom: labelLong ? 30 : 4 }}>
                            <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                            <XAxis dataKey="label" fontSize={10} stroke="hsl(var(--muted-foreground))"
                                tickFormatter={tickFormatter}
                                angle={xAxisAngle} textAnchor={xAxisTextAnchor} height={xAxisHeight} interval={0} />
                            <YAxis fontSize={10} stroke="hsl(var(--muted-foreground))" tickFormatter={fmtAxis} />
                            <Tooltip formatter={(v) => fmtTooltip(v, y_is_currency)}
                                contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }} />
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            {cfg.groups.map((g, i) => (
                                <Bar key={g} dataKey={g} fill={CHART_COLORS[i % CHART_COLORS.length]} radius={[2, 2, 0, 0]}
                                    /* ISSUE-FIX: Robustly support stacking groups in vertical BarChart from widget options/config. */
                                    stackId={w.options?.stacked || w.stacked || cfg.stacked ? "a" : undefined} />
                            ))}
                        </BarChart>
                    ) : (
                        <BarChart data={data} margin={{ top: 4, right: 12, left: 0, bottom: labelLong ? 30 : 4 }}>
                            <CartesianGrid strokeDasharray="2 4" stroke="hsl(var(--border))" />
                            <XAxis dataKey="label" fontSize={10} stroke="hsl(var(--muted-foreground))"
                                tickFormatter={tickFormatter}
                                angle={xAxisAngle} textAnchor={xAxisTextAnchor} height={xAxisHeight} interval={0} />
                            <YAxis fontSize={10} stroke="hsl(var(--muted-foreground))" tickFormatter={fmtAxis} />
                            <Tooltip formatter={tooltipFmtWithName}
                                contentStyle={{ backgroundColor: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 2, fontSize: 12 }} />
                            {/* iter-121.104 — Render legend so `legend_label` (and the default title fallback) is visible. */}
                            <Legend wrapperStyle={{ fontSize: 11 }} />
                            <Bar dataKey="value" name={legendName} radius={[2, 2, 0, 0]}>
                                {data.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                            </Bar>
                        </BarChart>
                    )}
                </ResponsiveContainer>
            </div>
        </div>
    );
}


export function TableWidget({ w }) {
    const cfg = w.config || {};
    const columns = cfg.columns || [];
    const rows = cfg.rows || [];
    // iter-119al — same empty-state treatment as ChartWidget so the user
    // sees a clear message instead of an empty table header on fresh
    // dashboards populated from Gallery.
    if (!rows.length) {
        return (
            <div className="dash-card p-5 h-full flex flex-col" data-testid={`widget-${w.id}`}>
                <div className="flex items-start justify-between mb-1">
                    <div className="eyebrow">{w.title}</div>
                    {cfg.stale && <StaleBadge reason={cfg.stale_reason} />}
                </div>
                <div className="flex-1 mt-3 min-h-0 flex flex-col items-center justify-center text-center gap-2 px-4">
                    <div className="text-xs text-muted-foreground max-w-sm">No rows yet for this widget.</div>
                    <div className="text-[10px] font-mono text-muted-foreground/70 uppercase tracking-wider">
                        Connect a source or wait for the next sync to populate it.
                    </div>
                </div>
            </div>
        );
    }

    const showTotal = w.options?.show_total || w.options?.show_totals || w.options?.total_row || w.options?.show_total_row || w.options?.show_totals_row || cfg.show_total || cfg.show_totals || cfg.total_row || cfg.show_total_row || cfg.show_totals_row || (w.config?.options && (w.config.options.show_total || w.config.options.show_totals || w.config.options.total_row || w.config.options.show_total_row || w.config.options.show_totals_row));

    const totals = {};
    if (showTotal) {
        let firstNonNumericCol = null;
        
        // Helper to find column metadata
        const getColMetadata = (colLabel) => {
            const tableCols = cfg.table_columns || [];
            let match = tableCols.find(col => col.label === colLabel);
            if (match) return match;
            const wCols = w.columns || [];
            match = wCols.find(col => col.label === colLabel || col.name === colLabel);
            return match || {};
        };

        columns.forEach((c) => {
            const values = [];
            let hasDecimal = false;

            for (const r of rows) {
                const val = r[c];
                if (val !== undefined && val !== null && val !== "") {
                    const num = Number(val);
                    if (!isNaN(num)) {
                        values.push(num);
                        if (String(val).includes(".")) {
                            hasDecimal = true;
                        }
                    }
                }
            }

            // Determine if column is numeric: it has numeric values, and at least one row has a value
            const colMeta = getColMetadata(c);
            const hasValues = rows.some(r => r[c] !== undefined && r[c] !== null && r[c] !== "");
            const allNumeric = hasValues && values.length === rows.filter(r => r[c] !== undefined && r[c] !== null && r[c] !== "").length;

            if (cfg?.grand_total && cfg.grand_total[c] !== undefined) {
                totals[c] = { isNumeric: true, val: cfg.grand_total[c], hasDecimal, format: colMeta?.format };
            } else if (allNumeric) {
                // ISSUE-FIX: Support COUNT_DISTINCT and calculated measures in TableWidget totals calculation
                const getAggType = (formula, agg) => {
                    if (agg) {
                        const a = String(agg).toLowerCase();
                        if (a === "avg" || a === "average" || a === "compound") return "avg";
                        if (a === "min" || a === "max") return a;
                        if (a === "count_distinct" || a === "distinct" || a === "count distinct" || a === "count_distinct_if") return "count_distinct";
                        if (a === "count" || a === "count_if") return "count";
                        return "sum";
                    }
                    if (!formula || typeof formula !== "string") return "sum";
                    const f = formula.trim().toUpperCase();
                    if (f.includes("/") || f.includes("*") || f.split("SUM").length > 2 || f.split("COUNT").length > 2) {
                        return "avg";
                    }
                    if (f.startsWith("AVG") || f.startsWith("AVERAGE")) return "avg";
                    if (f.startsWith("MIN")) return "min";
                    if (f.startsWith("MAX")) return "max";
                    if (f.includes("DISTINCT") || f.startsWith("COUNT_DISTINCT")) return "count_distinct";
                    if (f.startsWith("COUNT")) return "count";
                    return "sum";
                };

                const aggType = getAggType(colMeta.formula, colMeta.agg);
                let result = 0;
                
                if (aggType === "avg" || aggType === "average" || aggType === "compound") {
                    const sum = values.reduce((a, b) => a + b, 0);
                    result = values.length ? (sum / values.length) : 0;
                    hasDecimal = true;
                } else if (aggType === "min") {
                    result = values.length ? Math.min(...values) : 0;
                } else if (aggType === "max") {
                    result = values.length ? Math.max(...values) : 0;
                } else if (aggType === "count_distinct") {
                    result = new Set(values).size;
                } else if (aggType === "count") {
                    result = values.length;
                } else {
                    // Default to sum
                    result = values.reduce((a, b) => a + b, 0);
                }

                totals[c] = { isNumeric: true, val: result, hasDecimal, format: colMeta?.format };
            } else {
                totals[c] = { isNumeric: false, val: "" };
                if (firstNonNumericCol === null) {
                    firstNonNumericCol = c;
                }
            }
        });

        // Determine which column gets the "GRAND TOTAL" label
        const labelCol = firstNonNumericCol !== null ? firstNonNumericCol : columns[0];

        // Format values for rendering
        columns.forEach((c) => {
            if (c === labelCol) {
                totals[c] = "GRAND TOTAL";
            } else if (totals[c].isNumeric) {
                const { val, hasDecimal, format } = totals[c];
                if (format) {
                    const dec = typeof format.decimals === "number" ? format.decimals : 2;
                    if (format.percent) {
                        const mult = val <= 1.0 && val >= -1.0 ? 100 : 1;
                        totals[c] = (val * mult).toFixed(dec) + "%";
                    } else if (format.currency) {
                        totals[c] = "$" + val.toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec });
                    } else {
                        totals[c] = val.toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec });
                    }
                } else if (c.toLowerCase().includes("rate") || c.toLowerCase().includes("percent") || c.includes("%")) {
                    if (val <= 1.0 && val >= 0.0) {
                        totals[c] = (val * 100).toFixed(1) + "%";
                    } else {
                        totals[c] = val.toFixed(1) + "%";
                    }
                } else {
                    totals[c] = hasDecimal
                        ? val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : val.toLocaleString();
                }
            } else {
                totals[c] = "";
            }
        });
    }

    // Helper to find column metadata for cell formatting
    const getColMetadata = (colLabel) => {
        const tableCols = cfg.table_columns || [];
        let match = tableCols.find(col => col.label === colLabel);
        if (match) return match;
        const wCols = w.columns || [];
        match = wCols.find(col => col.label === colLabel || col.name === colLabel);
        return match || {};
    };

    // ISSUE-FIX: Format table cell values (percentages, currency, decimals) according to column metadata
    const formatTableCell = (val, colName) => {
        if (val === undefined || val === null || val === "") return "";
        if (typeof val === "number" || (!isNaN(Number(val)) && typeof val !== "string" && String(val).trim() !== "")) {
            const num = Number(val);
            const colMeta = getColMetadata(colName);
            const format = colMeta?.format || cfg?.format;
            if (format) {
                const dec = typeof format.decimals === "number" ? format.decimals : 2;
                if (format.percent) {
                    const mult = num <= 1.0 && num >= -1.0 ? 100 : 1;
                    return (num * mult).toFixed(dec) + "%";
                }
                if (format.currency) {
                    return "$" + num.toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec });
                }
                return num.toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec });
            }
            if (colName.toLowerCase().includes("rate") || colName.toLowerCase().includes("percent") || colName.includes("%")) {
                if (num <= 1.0 && num >= 0.0) {
                    return (num * 100).toFixed(1) + "%";
                }
            }
        }
        return String(val);
    };

    return (
        <div className="dash-card p-5 h-full flex flex-col" data-testid={`widget-${w.id}`}>
            <div className="flex items-start justify-between mb-1">
                <div className="eyebrow">{w.title}</div>
                {cfg.stale && <StaleBadge reason={cfg.stale_reason} />}
            </div>
            <div className="mt-3 flex-1 min-h-0 overflow-auto scrollbar-thin">
                <table className="w-full text-xs font-mono dash-table">
                    <thead className="sticky top-0 bg-card">
                        <tr className="text-left text-muted-foreground">
                            {columns.map((c) => (<th key={c} className="px-2 py-1.5 border-b border-border font-medium uppercase text-[10px] tracking-wider">{c}</th>))}
                        </tr>
                        {showTotal && (
                            <tr className="border-t border-border font-bold text-foreground" data-testid={`widget-${w.id}-total-row`}>
                                {columns.map((c) => (
                                    <th key={c} className="px-2 py-2 text-left font-bold border-b border-border bg-card">
                                        {totals[c]}
                                    </th>
                                ))}
                            </tr>
                        )}
                    </thead>
                    <tbody>
                        {rows.map((r, i) => (
                            <tr key={i} className="hover:bg-secondary/40">
                                {columns.map((c) => (<td key={c} className="px-2 py-1.5 border-b border-border/50">{formatTableCell(r[c], c)}</td>))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
